import { yupResolver } from '@hookform/resolvers/yup'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'

import { Alert } from '../../components/Alert'
import { AuthLayout } from '../../components/AuthLayout'
import { Button } from '../../components/Button'
import { PasswordInput } from '../../components/FormElements/PasswordInput'
import { FeedbackModal } from '../../components/Modal/FeebackModal'
import { useAuth } from '../../contexts/AuthContext'
import { ErrorCode } from '../../enumns/errors'
import { api } from '../../services/api'
import { createResetPasswordSchema } from '../../validators/resetPasswordSchema'

type FormValues = {
  password: string
  passwordConfirmation: string
}

type FeedbackState = string | null | undefined

const schema = createResetPasswordSchema({
  password: {
    min: 'INVALID_PASSWORD',
    matches: 'INVALID_PASSWORD',
    identical: 'PASSWORDS_MUST_BE_IDENTICAL',
    required: 'REQUIRED_FIELD'
  },
  passwordConfirmation: {
    identical: 'PASSWORDS_MUST_BE_IDENTICAL',
    required: 'REQUIRED_FIELD'
  }
})

export const ResetPassword = () => {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const { signIn, signOut } = useAuth()

  const [searchParams] = useSearchParams()
  const [intlErrorCode, setIntlErrorCode] = useState<FeedbackState>()
  const [intlSuccessCode, setIntlSuccessCode] = useState<FeedbackState>()

  const { control, formState, handleSubmit } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const onSubmit = async (values: FormValues) => {
    const token = searchParams.get('token')

    try {
      const response = await api.post('/auth/reset-password', {
        ...values,
        token,
      })

      await signIn({
        email: response.data.email,
        password: values.password,
      })

      setIntlSuccessCode('PASSWORD_SUCCESSFULLY_RESET')
    } catch (error) {
      console.error(error)

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          return setIntlErrorCode(ErrorCode.INVALID_PASSWORD_CHANGE_CODE)
        }

        if (error.response?.status === 400) {
          return setIntlErrorCode(ErrorCode.PASSWORD_RESET_LINK_EXPIRED)
        }
      }

      const message = formatMessage({ id: ErrorCode.UNKNOWN })
      setIntlErrorCode(message)
    }
  }

  useEffect(() => {
    if (formState.isSubmitting) {
      setIntlErrorCode(null)
      setIntlSuccessCode(null)
    }
  }, [formState.isSubmitting])

  useEffect(() => {
    signOut()
  }, [])

  return (
    <>
      <AuthLayout
        title={formatMessage({ id: 'REGISTER_NEW_PASSWORD' })}
        withNavbar
      >
        {intlErrorCode && (
          <div className="mb-4">
            <Alert
              variant="error"
              fullWidth
              onClose={() => setIntlErrorCode(null)}
            >
              {formatMessage({ id: intlErrorCode })}
            </Alert>
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col justify-between h-full grow"
        >
          <section className="md:mb-14">
            <div className="mb-4">
              <Controller
                name="password"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { invalid, error },
                }) => (
                  <PasswordInput
                    isValid={!invalid}
                    label={formatMessage({ id: 'ENTER_YOUR_NEW_PASSWORD' })}
                    autoComplete="new-password"
                    value={value}
                    onChange={onChange}
                    errorMessage={
                      error?.message && formatMessage({ id: error.message })
                    }
                  />
                )}
              />
            </div>

            <div>
              <Controller
                name="passwordConfirmation"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { invalid, error },
                }) => (
                  <PasswordInput
                    isValid={!invalid}
                    label={formatMessage({ id: 'TYPE_PASSWORD_AGAIN' })}
                    autoComplete="new-password"
                    value={value}
                    onChange={onChange}
                    errorMessage={
                      error?.message && formatMessage({ id: error.message })
                    }
                  />
                )}
              />
            </div>
          </section>

          <footer className="flex flex-col gap-2 md:flex-row-reverse">
            <div className="flex-1">
              <Button
                disabled={!formState.isValid}
                isLoading={formState.isSubmitting}
                type="submit"
                fullWidth
              >
                {formatMessage({ id: 'SAVE' })}
              </Button>
            </div>

            <div className="flex-1">
              <NavLink to={'/expert'}>
                <Button
                  disabled={formState.isSubmitting}
                  type="button"
                  variant="outline"
                  fullWidth
                >
                  {formatMessage({ id: 'CANCEL' })}
                </Button>
              </NavLink>
            </div>
          </footer>
        </form>
      </AuthLayout>

      <FeedbackModal
        isOpen={!!intlSuccessCode}
        variant="success"
        message={formatMessage({ id: intlSuccessCode || 'SUCCESS' })}
        onConfirm={() => navigate('/expert')}
        onClose={() => setIntlSuccessCode(null)}
        buttonLabels={{
          confirm: 'Login',
          cancel: 'Cancelar',
        }}
      />
    </>
  )
}
